import React, { useId, useRef } from "react";
import { T } from "../app/tokens.js";
import { HeadingTooltip } from "./HeadingTooltip.jsx";
import { useHoverIntent } from "./useHoverIntent.js";
import { HowWeDefineThis } from "./SpacePolicyDefinitions.jsx";
import { FIT_STYLE, seatFitBands } from "../services/seatFit.js";
import { useSpacePolicy } from "../services/spacePolicyStore.js";

// Opt-in for column headers only. Other tooltip surfaces retain their styling.
export const LIGHT_HEADER_TOOLTIP_STYLE = {
  background: T.white, color: T.dark, border: `1px solid ${T.border}`,
  boxShadow: T.shadowFloat, borderRadius: 8, padding: "10px 13px",
  fontSize: T.fsXs, fontWeight: T.fwRegular, lineHeight: 1.5,
  whiteSpace: "normal", textAlign: "left", textTransform: "none",
  letterSpacing: 0, width: 300, maxWidth: "calc(100vw - 24px)",
  boxSizing: "border-box", zIndex: 9999, pointerEvents: "auto",
};

export function SpaceFitHeaderCopy() {
  const { baseline } = useSpacePolicy();
  return <div data-testid="space-fit-header-copy">
    <p style={{ margin: "0 0 8px" }}>Space Fit: assigned today ÷ installed seats.</p>
    {seatFitBands(baseline).map(({ status, label, description: range }) => <div key={status} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
      <span style={{ background: FIT_STYLE[status].bg, color: FIT_STYLE[status].ink,
        border: `1px solid ${FIT_STYLE[status].border}`, borderRadius: 6, padding: "1px 6px", minWidth: 82, textAlign: "center" }}>{label}</span>
      <span>{range}</span>
    </div>)}
    <p style={{ margin: "8px 0" }}>Operational and dormant offices only.</p>
    <HowWeDefineThis />
  </div>;
}

// Clone the existing control rather than adding a grid track or nested button.
export function ColumnHeaderExplanation({ children, content, testId }) {
  const ref = useRef(null);
  const id = useId();
  const intent = useHoverIntent({ interactive: true });
  const child = React.Children.only(children);
  return <>
    {React.cloneElement(child, {
      ref, title: undefined,
      ...intent.triggerProps,
      "aria-describedby": intent.open ? id : undefined,
      tabIndex: child.props.tabIndex ?? 0,
      onKeyDown: event => {
        intent.triggerProps.onKeyDown?.(event);
        if (child.props.onKeyDown) child.props.onKeyDown(event);
        else if (child.type !== "button" && event.target === event.currentTarget && ["Enter", " "].includes(event.key)) {
          event.preventDefault();
          child.props.onClick?.(event);
        }
      },
    })}
    {intent.open && <HeadingTooltip triggerRef={ref} role="tooltip"
      id={id} data-testid={testId} {...intent.popupProps}
      onKeyDown={event => {
        if (event.key === "Escape") ref.current?.focus({ preventScroll: true });
        intent.popupProps.onKeyDown?.(event);
      }}
      style={LIGHT_HEADER_TOOLTIP_STYLE}>
      {content}
    </HeadingTooltip>}
  </>;
}
